import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import {
  Alert,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
  Tabs,
} from '@/components/ui'
import {
  fetchTvAnalyticsResumo,
  fetchTvCampanhas,
  fetchTvMidiasAtivas,
  fetchTvPlaylists,
} from './api'

type TabId = 'midias' | 'playlists' | 'campanhas'

export function TvPage() {
  const [tab, setTab] = useState<TabId>('midias')

  const midias = useQuery({
    queryKey: ['tv', 'midias-ativas'],
    queryFn: () => fetchTvMidiasAtivas(),
  })
  const playlists = useQuery({
    queryKey: ['tv', 'playlists'],
    queryFn: () => fetchTvPlaylists(),
    enabled: tab === 'playlists',
  })
  const campanhas = useQuery({
    queryKey: ['tv', 'campanhas'],
    queryFn: () => fetchTvCampanhas(true),
    enabled: tab === 'campanhas',
  })
  const analytics = useQuery({
    queryKey: ['tv', 'analytics'],
    queryFn: () => fetchTvAnalyticsResumo(),
  })

  const lista = midias.data || []

  return (
    <section className="hi-page">
      <PageHeader
        title="TV corporativa"
        subtitle="Mídias, playlists e campanhas via API /api/v1/endomarketing/."
        actions={
          <a className="btn btn-secondary" href={legacyAppUrl('#/tv-corporativa')}>
            Legado
          </a>
        }
      />

      <KpiGrid>
        <Kpi label="Mídias ativas" value={String(lista.length)} />
        <Kpi
          label="Total de mídias"
          value={String(analytics.data?.total_midias ?? '—')}
        />
        <Kpi
          label="Visualizações"
          value={String(analytics.data?.total_visualizacoes ?? '—')}
        />
      </KpiGrid>

      <Tabs
        label="Seções da TV"
        value={tab}
        onChange={setTab}
        items={[
          { id: 'midias', label: 'Mídias ativas' },
          { id: 'playlists', label: 'Playlists' },
          { id: 'campanhas', label: 'Campanhas' },
        ]}
      />

      {tab === 'midias' ? (
        <Section title="Em exibição" flush>
          {midias.isLoading ? <Muted>Carregando mídias…</Muted> : null}
          {midias.isError ? (
            <Alert tone="danger">
              {midias.error instanceof ApiError
                ? midias.error.message
                : 'Falha ao carregar mídias da TV.'}
            </Alert>
          ) : null}
          {!midias.isLoading && lista.length === 0 ? (
            <Empty>Nenhuma mídia ativa no momento. Cadastre conteúdo no legado ou admin.</Empty>
          ) : null}
          <ul className="hi-list">
            {lista.map((m) => (
              <li key={String(m.id)} className="card hi-row">
                <div className="hi-row-main">
                  <div className="hi-actions" style={{ justifyContent: 'space-between' }}>
                    <strong>{m.titulo || 'Mídia'}</strong>
                    <StatusBadge tone="ativo">{m.categoria_label || m.categoria || '—'}</StatusBadge>
                  </div>
                  <Muted>
                    {m.tipo_label || m.tipo || '—'}
                    {m.setor ? ` · ${m.setor}` : ''}
                    {` · ${m.visualizacoes ?? 0} views`}
                    {m.duracao ? ` · ${m.duracao}s` : ''}
                  </Muted>
                  {m.descricao ? <p className="hi-muted">{m.descricao}</p> : null}
                  {m.url ? (
                    <a className="btn btn-secondary btn-sm" href={m.url} target="_blank" rel="noreferrer">
                      Abrir mídia
                    </a>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {tab === 'playlists' ? (
        <Section title="Playlists" flush>
          {playlists.isLoading ? <Muted>Carregando…</Muted> : null}
          {playlists.isError ? (
            <Alert tone="danger">
              {playlists.error instanceof ApiError
                ? playlists.error.message
                : 'Falha ao carregar playlists.'}
            </Alert>
          ) : null}
          {!playlists.isLoading && (playlists.data || []).length === 0 ? (
            <Empty>Nenhuma playlist cadastrada.</Empty>
          ) : null}
          <ul className="hi-list">
            {(playlists.data || []).map((p) => (
              <li key={String(p.id)} className="card hi-row">
                <div className="hi-row-main">
                  <strong>{p.nome}</strong>
                  <Muted>
                    {p.total_midias ?? 0} mídias
                    {p.duracao_total ? ` · ${p.duracao_total}s` : ''}
                    {p.ativa === false ? ' · inativa' : ''}
                    {p.loop ? ' · loop' : ''}
                  </Muted>
                  {p.descricao ? <p className="hi-muted">{p.descricao}</p> : null}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {tab === 'campanhas' ? (
        <Section title="Campanhas ativas" flush>
          {campanhas.isLoading ? <Muted>Carregando…</Muted> : null}
          {campanhas.isError ? (
            <Alert tone="danger">
              {campanhas.error instanceof ApiError
                ? campanhas.error.message
                : 'Falha ao carregar campanhas.'}
            </Alert>
          ) : null}
          {!campanhas.isLoading && (campanhas.data || []).length === 0 ? (
            <Empty>Nenhuma campanha ativa no período.</Empty>
          ) : null}
          <ul className="hi-list">
            {(campanhas.data || []).map((c) => (
              <li key={String(c.id)} className="card hi-row">
                <div className="hi-row-main">
                  <div className="hi-actions" style={{ justifyContent: 'space-between' }}>
                    <strong>{c.nome}</strong>
                    <StatusBadge tone={c.ativa ? 'ativo' : 'inativo'}>
                      {c.status_label || c.status || '—'}
                    </StatusBadge>
                  </div>
                  <Muted>
                    {c.playlist_nome || 'Sem playlist'}
                    {c.inicio ? ` · ${new Date(c.inicio).toLocaleDateString('pt-BR')}` : ''}
                    {c.fim ? ` → ${new Date(c.fim).toLocaleDateString('pt-BR')}` : ''}
                  </Muted>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </section>
  )
}
